import type { ActionKey, AuthUser, ModuleKey } from '@fluxus/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { env } from '../env';
import { detectPlatform, getSecureStorage } from '../platform';
import { can } from './access';
import { AuthClient, type AuthSession, type LoginOutcome } from './auth-client';

/** Sessão do app na plataforma atual (web: cookie httpOnly; desktop/celular: armazenamento seguro). */
export function createAuthClient(): AuthClient {
  const platform = detectPlatform();
  return new AuthClient({ platform, storage: getSecureStorage(platform), baseUrl: env.apiUrl });
}

export type AuthState =
  | { status: 'loading' }
  /** loggedOut: o usuário saiu (aqui ou em outra aba) — o login não deve "voltar" para a tela anterior. */
  | { status: 'anonymous'; loggedOut?: boolean }
  | { status: 'error'; error: unknown }
  | { status: 'authenticated'; session: AuthSession };

type AuthContextValue = {
  state: AuthState;
  client: AuthClient;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  /** Tenta restaurar a sessão de novo (após falha de rede ao abrir o app). */
  retry: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ client, children }: { client: AuthClient; children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>(() =>
    client.current ? { status: 'authenticated', session: client.current } : { status: 'loading' },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(
    () =>
      client.subscribe((session, change) => {
        if (session) {
          setState({ status: 'authenticated', session });
        } else {
          // Saiu (aqui ou em outra aba) ou a sessão terminou: nada do usuário anterior fica em cache.
          queryClient.clear();
          setState({ status: 'anonymous', loggedOut: change === 'logout' });
        }
      }),
    [client, queryClient],
  );

  // Ao abrir o app, restaura a sessão (cookie na web, armazenamento seguro nos apps).
  useEffect(() => {
    // A sessão pode ter chegado de outra aba (BroadcastChannel) entre a criação deste provedor e a
    // inscrição acima: nesse caso a notificação já passou e a tela precisa ser sincronizada aqui.
    const existing = client.current;
    if (existing) {
      setState({ status: 'authenticated', session: existing });
      return;
    }
    let active = true;
    client.refresh().then(
      (session) => {
        if (active) setState(session ? { status: 'authenticated', session } : { status: 'anonymous' });
      },
      (error: unknown) => {
        if (active) setState({ status: 'error', error });
      },
    );
    return () => {
      active = false;
    };
  }, [client, attempt]);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);
  const login = useCallback((email: string, password: string) => client.login(email, password), [client]);
  const logout = useCallback(() => client.logout(), [client]);

  const value = useMemo(
    () => ({ state, client, login, logout, retry }),
    [state, client, login, logout, retry],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return ctx;
}

/** Usuário logado (só dentro das rotas protegidas). */
export function useCurrentUser(): AuthUser {
  const { state } = useAuth();
  if (state.status !== 'authenticated') throw new Error('useCurrentUser fora de uma rota protegida.');
  return state.session.user;
}

/**
 * `can(module, action)` do usuário logado: telas escondem ou desabilitam ações sem permissão.
 * A API confere de novo em cada rota — esconder na tela é conforto, não segurança.
 */
export function useCan(): (module: ModuleKey, action: ActionKey) => boolean {
  const { state } = useAuth();
  // Sem sessão (ainda carregando ou saindo), nenhuma ação é permitida.
  const user = state.status === 'authenticated' ? state.session.user : null;
  return (module, action) => (user ? can(user, module, action) : false);
}
